import React, { useEffect, useMemo } from "react";
import { ProductWithSales } from "../../utils/profitabilityB2B";
import { PurchaseOrder } from "../../services/purchaseOrderService";
import {
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";
import { Input, Typography, Row, Col, Button, Tag, Divider, Empty, InputNumber } from "antd";
import { useLocationCosts } from "../../hooks/useLocationCosts";
import { 
  Plus, 
  Trash2, 
  PieChart as PieIcon,
  Activity
} from "lucide-react";
import dayjs from "dayjs";

const { Title, Text } = Typography;

interface MetricsSectionProps {
  products: ProductWithSales[];
  startDate: Date;
  endDate: Date;
  orders: PurchaseOrder[];
  locationId: string;
}

const MetricsSection: React.FC<MetricsSectionProps> = ({
  products,
  startDate,
  endDate,
  orders,
  locationId,
}) => {
  const {
    costs: costInputs,
    initialize,
    addNewCost,
    updateExistingCost,
    removeExistingCost,
  } = useLocationCosts(locationId);

  useEffect(() => {
    initialize();
  }, [initialize, locationId]);

  // CALCULATION LOGIC
  const totalSales = useMemo(() => products.reduce((sum, p) => sum + p.totalSales, 0), [products]);
  const totalCost = useMemo(() => products.reduce((sum, p) => sum + p.totalCost, 0), [products]);
  const totalLogisticsCost = useMemo(() => orders.reduce((sum, order) => 
    sum + Number(order.importCost ?? order.estimatedCost ?? order.shippingCost ?? 0), 0
  ), [orders]);

  const baseCommission = costInputs.find((input) => input.isBaseCommission);
  const baseCommissionRate = baseCommission ? baseCommission.value / 100 : 0;
  const baseCommissionAmount = totalSales * baseCommissionRate;

  const grossProfit = totalSales - totalCost;
  
  const variableCosts = costInputs
    .filter((input) => input.type === "percentage" && !input.isBaseCommission)
    .reduce((sum, input) => sum + (totalSales * input.value) / 100, 0);

  const fixedCosts = costInputs
    .filter((input) => input.type === "fixed")
    .reduce((sum, input) => sum + input.value, 0);

  const totalOrders = orders.length;
  const perOrderCosts = costInputs
    .filter((input) => input.type === "per_order")
    .reduce((sum, input) => sum + input.value * totalOrders, 0);

  const netProfit = totalSales - totalCost - baseCommissionAmount - variableCosts - fixedCosts - perOrderCosts - totalLogisticsCost;
  const netMargin = totalSales > 0 ? (netProfit / totalSales) * 100 : 0;

  // PIE DATA
  const pieData = useMemo(() => {
    const data = [
      { name: "Marchandises", value: totalCost, color: "#94a3b8" },
      { name: "Distribution", value: baseCommissionAmount, color: "#6366f1" },
      { name: "Logistique", value: totalLogisticsCost, color: "#f59e0b" },
      { name: "Opérationnel", value: variableCosts + fixedCosts + perOrderCosts, color: "#ec4899" },
      { name: "Profit Net", value: Math.max(0, netProfit), color: "#10b981" },
    ].filter(d => d.value > 0);
    return data;
  }, [totalCost, baseCommissionAmount, totalLogisticsCost, variableCosts, fixedCosts, perOrderCosts, netProfit]);

  // TREND DATA
  const daysBetween = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) || 1;
  
  const profitTrendData = useMemo(() => {
    const salesByDay = new Map<string, { sales: number; cost: number }>();
    
    // Init dates
    for (let i = 0; i <= daysBetween; i++) {
      const d = new Date(startDate);
      d.setDate(d.getDate() + i);
      salesByDay.set(d.toISOString().split("T")[0], { sales: 0, cost: 0 });
    }

    orders.forEach(order => {
      const dateKey = new Date(order.creationDate || order.orderDate || Date.now()).toISOString().split("T")[0];
      if (salesByDay.has(dateKey)) {
        const entry = salesByDay.get(dateKey)!;
        entry.sales += Number(order.totalAmount) || 0;
        // Approximation simple du coût pour le graph d'évolution
        const ratio = totalSales > 0 ? (Number(order.totalAmount) || 0) / totalSales : 0;
        entry.cost += (totalCost + totalLogisticsCost + baseCommissionAmount + variableCosts + fixedCosts + perOrderCosts) * ratio;
      }
    });

    return Array.from(salesByDay.entries()).map(([date, val]) => ({
      name: dayjs(date).format("DD MMM"),
      revenue: val.sales,
      profit: val.sales - val.cost,
    })).sort((a, b) => a.name.localeCompare(b.name));
  }, [orders, startDate, daysBetween, totalSales, totalCost, totalLogisticsCost, baseCommissionAmount, variableCosts, fixedCosts, perOrderCosts]);

  const formatCurrency = (val: number) => 
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(val);

  return (
    <div className="metrics-section-refined">
      <Row gutter={[24, 24]}>
        {/* LEFT COLUMN: PNL OVERVIEW */}
        <Col xs={24} xl={16}>
          <div className="analytics-card main-chart-card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Activity size={18} className="text-blue-600" />
                <Title level={4} style={{ margin: 0 }}>Analyse de la Rentabilité Nette</Title>
              </div>
              <Tag color={netProfit >= 0 ? "success" : "error"} className="border-0 font-bold px-3">
                Marge Nette: {netMargin.toFixed(1)}%
              </Tag>
            </div>
            
            <div className="chart-container" style={{ height: 320, marginTop: 24 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={profitTrendData}>
                  <defs>
                    <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorProfit" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.1}/>
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                  <XAxis 
                    dataKey="name" 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 12 }} 
                    dy={10}
                  />
                  <YAxis 
                    axisLine={false} 
                    tickLine={false} 
                    tick={{ fill: '#94a3b8', fontSize: 12 }}
                    tickFormatter={(val) => `${val}€`}
                  />
                  <Tooltip 
                    contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                    formatter={(val: number) => [formatCurrency(val), ""]}
                  />
                  <Area type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" name="Chiffre d'Affaires" />
                  <Area type="monotone" dataKey="profit" stroke="#10b981" strokeWidth={3} fillOpacity={1} fill="url(#colorProfit)" name="Profit Net" />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="pnl-breakdown">
              <Row gutter={16}>
                <Col span={6}>
                  <div className="pnl-stat">
                    <Text type="secondary" className="uppercase text-[10px] font-bold">Brut</Text>
                    <Title level={4} style={{ margin: 0 }}>{formatCurrency(totalSales)}</Title>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="pnl-stat">
                    <Text type="secondary" className="uppercase text-[10px] font-bold">Logistique</Text>
                    <Title level={4} style={{ margin: 0, color: '#f59e0b' }}>-{formatCurrency(totalLogisticsCost)}</Title>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="pnl-stat">
                    <Text type="secondary" className="uppercase text-[10px] font-bold">Opérationnel</Text>
                    <Title level={4} style={{ margin: 0, color: '#ec4899' }}>-{formatCurrency(variableCosts + fixedCosts + perOrderCosts)}</Title>
                  </div>
                </Col>
                <Col span={6}>
                  <div className="pnl-stat highlight">
                    <Text type="secondary" className="uppercase text-[10px] font-bold">Net Final</Text>
                    <Title level={4} style={{ margin: 0, color: netProfit >= 0 ? '#10b981' : '#ef4444' }}>{formatCurrency(netProfit)}</Title>
                  </div>
                </Col>
              </Row>
            </div>
          </div>
        </Col>

        {/* RIGHT COLUMN: COST MIX & SIMULATOR */}
        <Col xs={24} xl={8}>
          <div className="analytics-card">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <PieIcon size={18} className="text-purple-600" />
                <Title level={4} style={{ margin: 0 }}>Mix de Coûts</Title>
              </div>
            </div>
            
            <div style={{ height: 240, marginTop: 12 }}>
              {pieData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} stroke="none" />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)' }}
                      formatter={(val: number) => formatCurrency(val)}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Données insuffisantes" />}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div className="cost-simulator">
              <div className="flex justify-between items-center mb-4">
                <Title level={5} style={{ margin: 0 }}>Simulateur de Coûts</Title>
                <Button type="text" icon={<Plus size={14} />} onClick={addNewCost} size="small" className="text-blue-600 font-bold">Ajouter</Button>
              </div>
              
              <div className="cost-inputs-list">
                {costInputs.map(input => (
                  <div key={input.id} className="cost-item-row">
                    <div className="flex-1">
                      <Input 
                        variant="borderless" 
                        value={input.name} 
                        onChange={e => updateExistingCost(input.id, "name", e.target.value)}
                        className="cost-name-input"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <InputNumber
                        variant="borderless"
                        value={input.value}
                        onChange={(v: number | null) => updateExistingCost(input.id, "value", v || 0)}
                        className="cost-value-input"
                        suffix={input.type === 'percentage' ? '%' : '€'}
                      />
                      {!input.isBaseCommission && (
                        <Button 
                          type="text" 
                          danger 
                          icon={<Trash2 size={12} />} 
                          onClick={() => removeExistingCost(input.id)}
                          size="small"
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Col>
      </Row>

      <style>{`
        .analytics-card {
          background: #fff;
          border-radius: 24px;
          border: 1px solid #e2e8f0;
          padding: 24px;
          height: 100%;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
        }
        .main-chart-card {
          display: flex;
          flex-direction: column;
        }
        .card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        .pnl-breakdown {
          margin-top: auto;
          padding-top: 24px;
          border-top: 1px solid #f1f5f9;
        }
        .pnl-stat {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .pnl-stat.highlight {
          padding: 12px;
          background: #f8fafc;
          border-radius: 12px;
          margin-top: -12px;
        }
        .cost-item-row {
          display: flex;
          align-items: center;
          padding: 8px 12px;
          background: #f8fafc;
          border-radius: 12px;
          margin-bottom: 8px;
          transition: all 0.2s;
        }
        .cost-item-row:hover {
          background: #f1f5f9;
        }
        .cost-name-input {
          font-weight: 600;
          font-size: 13px;
          color: #475569;
          padding: 0;
        }
        .cost-value-input {
          width: 80px;
          font-weight: 700;
          color: #0f172a;
          text-align: right;
        }
        .cost-value-input .ant-input-number-input {
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default MetricsSection;
